import * as React from 'react';

import { IDisplayValue, ISize } from '../../ts/Interfaces';

const MIN_SELECTED_ITEMS_CELL_WIDTH = 30;

export function useElementsDivSizeHook(
    minSelectedItemsCellWidth: number,
    changeOnResize: boolean,
    elementsDivRef: React.RefObject<HTMLDivElement>
) {
    const elementsCellSize = React.useRef<ISize<number>>({ height: 0, width: 0 });
    const [, setUpdateFlag] = React.useState(0);

    const setElementsCellWidth = React.useCallback(
        (oldWidth: number) => {
            if (elementsDivRef.current != null) {
                const minWidth = minSelectedItemsCellWidth ?? MIN_SELECTED_ITEMS_CELL_WIDTH;

                let newWidth = elementsDivRef.current.clientWidth;

                if (newWidth < minWidth) {
                    newWidth = minWidth;
                }

                if (newWidth > 0 && oldWidth !== newWidth) {
                    let update = false;

                    if (elementsCellSize.current.height === 0) {
                        elementsCellSize.current.height = elementsDivRef.current.clientHeight;
                        update = true;
                    }

                    if (oldWidth === 0) {
                        elementsCellSize.current.width = newWidth;
                        update = true;
                    }

                    if (update) {
                        setUpdateFlag((updateFlag) => ++updateFlag);
                    }
                }
            }
        },
        [minSelectedItemsCellWidth]
    );

    React.useLayoutEffect(() => {
        setElementsCellWidth(elementsCellSize.current.width);
    }, [setElementsCellWidth]);

    React.useEffect(() => {
        let resizeObserver: ResizeObserver;

        if (changeOnResize && elementsDivRef.current != null) {
            resizeObserver = new ResizeObserver((entries) => {
                setElementsCellWidth(0);
            });

            resizeObserver.observe(elementsDivRef.current);
        }

        return () => {
            if (resizeObserver != null && elementsDivRef.current != null) {
                resizeObserver.unobserve(elementsDivRef.current);
            }
        };
    }, [setElementsCellWidth, changeOnResize]);

    return elementsCellSize.current;
}

export interface IUseDropdownSelectorStateHookState {
    value: IDisplayValue[];
    oldValue: IDisplayValue[];
    dropDownIsVisible: boolean;
}

export interface IUseDropdownSelectorStateHookObj {
    state: IUseDropdownSelectorStateHookState;
    setValue: (newValue: IDisplayValue[], makeItNotChanged: boolean) => void;
    makeValueNotChanged: () => void;
}

export function useDropdownSelectorStateHook(initialValue: IDisplayValue[]): IUseDropdownSelectorStateHookObj {
    const state = React.useRef<IUseDropdownSelectorStateHookState>({
        value: initialValue != null ? [...initialValue] : [],
        oldValue: initialValue != null ? [...initialValue] : [],
        dropDownIsVisible: false
    });
    const [, setUpdateFlag] = React.useState(0);

    const makeValueNotChanged = React.useCallback(() => {
        state.current.oldValue = [...state.current.value];
    }, []);

    const setValue = React.useCallback((newValue: IDisplayValue[], makeItNotChanged: boolean) => {
        state.current.value = newValue ?? [];

        if (makeItNotChanged) {
            makeValueNotChanged();
        }

        setUpdateFlag((updateFlag) => ++updateFlag);
    }, []);

    return { state: state.current, setValue, makeValueNotChanged };
}
